import {FC, useCallback, useMemo, useState} from 'react';
import {HeadingWithDecoration} from '../../../common/HeadingWithDecoration';
import {useUnitManagementSubscriptions} from './useUnitManagementSubscriptions';
import {MiddleTopBarUnitManage} from '../../../common/MiddleTopBarUnitManage';
import {IPersonaProps, Spinner, Text} from '@fluentui/react';
import {EnhancedDetailsList, OtherColumn, UserDtoColumn} from '../../../common/EnhancedDetailsList';
import {
  ColumnKey,
  IEnhancedColumn,
  SpecialColumnKey
} from '../../../common/EnhancedDetailsList/IEnhancedDetailsList';
import {PeoplePicker} from '../../../common/PeoplePicker';
import styles from './UnitManagementSubscriptions.module.scss';
import {Helmet} from 'react-helmet';
import {ProcessSubscription} from '../../../../models/ProcessSubscription';
import {TheButton} from '../../../common/TheButton';
import {ModalWithHeader} from '../../../common/ModalWithHeader';
import {on} from 'events';

export const UnitManagementSubscriptions: FC<{
  unitTitle: string | undefined;
}> = ({unitTitle}) => {
  const {
    processesWithSubscibers,
    unitId,
    isLoading,
    saveSubscriptions,
    modalOpen,
    setModalOpen,
    modifiedSubscriptions,
    setModifiedSubscriptions,
    modifiedBranchSubscriptions,
    setModifiedBranchSubscriptions
  } = useUnitManagementSubscriptions();

  const [modifiedItem, setModifiedItem] = useState<ProcessSubscription | null>(null);

  const onSubscriptionChange = useCallback(
    (people: IPersonaProps[] | undefined, processWithSubscibers: ProcessSubscription) => {
      const updated: ProcessSubscription = {
        ...processWithSubscibers,
        subscribersAzureIds: people?.map(p => p.id ?? '') || [],
        modified: true
      };

      setModifiedSubscriptions(prev => {
        const existingIndex = prev.findIndex(x => x.id === updated.id);
        if (existingIndex !== -1) {
          const newArray = [...prev];
          newArray[existingIndex] = updated;
          return newArray;
        } else {
          return [...prev, updated];
        }
      });
    },
    [setModifiedSubscriptions]
  );

  const onBranchSubscriptionChange = useCallback(
    (people: IPersonaProps[] | undefined, processWithSubscibers: ProcessSubscription) => {
      const updated: ProcessSubscription = {
        ...processWithSubscibers,
        branchSubscribersAzureIds: people?.map(p => p.id ?? '') || [],
        modified: true
      };
      setModifiedBranchSubscriptions(updated);
    },
    [setModifiedBranchSubscriptions]
  );

  const handleOpenModal = useCallback(
    (processWithSubscibers: ProcessSubscription) => {
      setModifiedItem(processWithSubscibers);
      setModalOpen(true);
    },
    [setModalOpen]
  );

  const handleCloseModal = useCallback(() => {
    setModifiedItem(null);
    setModalOpen(false);
    setModifiedBranchSubscriptions(null);
  }, [setModalOpen, setModifiedItem, setModifiedBranchSubscriptions]);

  const onSaveSubscriptions = useCallback(() => {
    saveSubscriptions(modifiedSubscriptions, modifiedBranchSubscriptions);
  }, [saveSubscriptions, modifiedSubscriptions, modifiedBranchSubscriptions]);

  const columns = useMemo(() => {
    const cols: IEnhancedColumn<ProcessSubscription>[] = [
      {
        key: ColumnKey.ProcessTitle,
        name: 'Process Title',
        flexGrow: 2,
        maxWidth: 550,
        onRender: item => <Text className={`${styles.title}`}>{item.processTitle}</Text>
      },
      {
        key: 'processOwnerAzureId',
        name: 'Process Owner',
        maxWidth: 200,
        flexGrow: 5,
        data: UserDtoColumn,
        onRender: item => (
          <PeoplePicker defaultSelectedPersonId={item.processOwnerAzureId} isReadonly />
        )
      },
      {
        key: 'subscribersAzureIds',
        name: 'Subscribers',
        maxWidth: 600,
        data: OtherColumn,
        onRender: item => {
          return (
            <PeoplePicker
              itemLimit={50}
              defaultSelectedPersonIds={item.subscribersAzureIds}
              onSelectPerson={people => {
                onSubscriptionChange(people, item);
              }}
            />
          );
        }
      },
      {
        key: SpecialColumnKey.Edit,
        name: 'Edit Branch Subscribers',
        onRender: (item: ProcessSubscription) => {
          return (
            <TheButton
              iconProps={{
                iconName: 'Edit'
              }}
              onClick={() => handleOpenModal(item)}
              disabled={false}
              primary>
              Branch Subscribers
            </TheButton>
          );
        },
        data: OtherColumn,
        minWidth: 200
      }
    ];
    return cols;
   }, [onSubscriptionChange, handleOpenModal]);

  const modalColumns = useMemo(() => {
    const cols: IEnhancedColumn<ProcessSubscription>[] = [
      {
        key: ColumnKey.ProcessTitle,
        name: 'Process Title',
        flexGrow: 2,
        maxWidth: 550,
        onRender: item => <Text className={`${styles.title}`}>{item.processTitle}</Text>
      },
      {
        key: 'processOwnerAzureId',
        name: 'Process Owner',
        maxWidth: 200,
        flexGrow: 5,
        data: UserDtoColumn,
        onRender: item => (
          <PeoplePicker defaultSelectedPersonId={item.processOwnerAzureId} isReadonly />
        )
      },
      {
        key: 'branchSubscribersAzureIds',
        name: 'Branch Subscribers',
        maxWidth: 700,
        data: OtherColumn,
        onRender: item => {
          return (
            <PeoplePicker
              itemLimit={50}
              defaultSelectedPersonIds={item.branchSubscribersAzureIds}
              onSelectPerson={people => {
                onBranchSubscriptionChange(people, item);
              }}
            />
          );
        }
      }
    ];
    return cols;
  }, [onBranchSubscriptionChange]);

  return (
    <main className={`${isLoading ? 'isLoading' : ''} noPaddingTop`}>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <MiddleTopBarUnitManage
            notUseHelmet
            unitId={unitId ? Number.parseInt(unitId) : undefined}
            active={'subscriptions'}
          />
          <HeadingWithDecoration text="Subscriptions" />
          {unitTitle && (
            <Helmet>
              <title>IMS {unitTitle} Subscriptions</title>
            </Helmet>
          )}
          <EnhancedDetailsList<ProcessSubscription, ProcessSubscription, ProcessSubscription>
            pageSize={50}
            columns={columns}
            items={processesWithSubscibers}
          />
          <div className={styles.subscriptions_button_container}>
            <TheButton primary onClick={onSaveSubscriptions}>
              {'Save'}
            </TheButton>
          </div>

          <ModalWithHeader
            header="Subscribe Branch"
            isVisible={modalOpen}
            dismiss={() => handleCloseModal()}>
            <EnhancedDetailsList<ProcessSubscription, ProcessSubscription, ProcessSubscription>
              hideSearch
              allowAdd={false}
              pageSize={50}
              columns={modalColumns}
              items={modifiedItem ? [modifiedItem] : []}
            />
            <div className={styles.subscriptions_modal_button_container}>
              <TheButton onClick={handleCloseModal}>{'Cancel'}</TheButton>
              <TheButton primary onClick={onSaveSubscriptions}>
                {'Save'}
              </TheButton>
            </div>
          </ModalWithHeader>
        </>
      )}
    </main>
  );
};
